import { z } from "zod/v4";
import { MarketShippingSelectionSchema } from "./shopify-shipping";

export const ShippingTestMethodDeleteTargetKindDtoSchema = z.enum([
  "canonical_method",
  "participant_method",
  "synthetic_free_condition",
  "synthetic_rate_definition",
]);

const ShippingTestMethodConditionValueDtoSchema = z.union([
  z.object({
    __typename: z.literal("MoneyV2"),
    amount: z.string(),
    currencyCode: z.string().nullish(),
  }),
  z.object({
    __typename: z.literal("Weight"),
    unit: z.string(),
    value: z.number(),
  }),
]);

export const ShippingTestMethodFreeConditionDtoSchema = z.object({
  subject: z.string(),
  min: ShippingTestMethodConditionValueDtoSchema.nullish(),
  max: ShippingTestMethodConditionValueDtoSchema.nullish(),
});

const legacyIdentifierSchema = z
  .string()
  .refine(
    (value) => !value.startsWith("gid://shopify/Market/"),
    "A market ID cannot be used as a legacy shipping identifier"
  );

/**
 * Each variant declares only its own fields. Columns that belong to the other
 * shipping model are unknown keys here, so a model row carrying `null` for them
 * parses without any caller-side scrubbing.
 */
export const LegacyShippingTestMethodDefinitionDtoSchema = z.object({
  id: z.uuid().optional(),
  shippingModel: z.literal("legacy").optional(),
  deliveryProfileId: legacyIdentifierSchema,
  deliveryLocationGroupId: legacyIdentifierSchema,
  deliveryZoneId: legacyIdentifierSchema,
  methodDefinitionId: z.string().nullish(),
  canonicalMethodId: z.string().optional(),
  deleteTargetKind: ShippingTestMethodDeleteTargetKindDtoSchema.optional(),
  deleteTargetRateDefinitionId: z.string().optional(),
  deleteTargetFreeCondition:
    ShippingTestMethodFreeConditionDtoSchema.optional(),
});

export const MarketShippingTestMethodDefinitionDtoSchema = z.object({
  id: z.uuid().optional(),
  shippingModel: z.literal("markets"),
  marketTarget: MarketShippingSelectionSchema,
});

export const ShippingTestMethodDefinitionDtoSchema = z.discriminatedUnion(
  "shippingModel",
  [
    LegacyShippingTestMethodDefinitionDtoSchema,
    MarketShippingTestMethodDefinitionDtoSchema,
  ]
);

export type LegacyShippingTestMethodDefinitionDto = z.infer<
  typeof LegacyShippingTestMethodDefinitionDtoSchema
>;

export type MarketShippingTestMethodDefinitionDto = z.infer<
  typeof MarketShippingTestMethodDefinitionDtoSchema
>;

export type ShippingTestMethodDefinitionDto = z.infer<
  typeof ShippingTestMethodDefinitionDtoSchema
>;

/** Legacy is the default model, so an absent discriminant means legacy. */
export const isLegacyShippingSelection = (
  selection: ShippingTestMethodDefinitionDto
): selection is LegacyShippingTestMethodDefinitionDto =>
  selection.shippingModel !== "markets";

export const isMarketShippingSelection = (
  selection: ShippingTestMethodDefinitionDto
): selection is MarketShippingTestMethodDefinitionDto =>
  selection.shippingModel === "markets";
